import { Document } from '@/interface/chat';
import { Grid2 } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import DocumentCard from './DocumentCard';

interface DocumentsListProps {
  documents: Document[];
}

const DOCUMENT_STRIP_THRESHOLD = 3;
const STRIP_CARD_WIDTH = 300;
const STRIP_CARD_WIDTH_XS = 260;

const DocumentsList: React.FC<DocumentsListProps> = ({ documents }) => {
  const theme = useTheme();
  const { t } = useTranslation(['chat']);
  // Filter out documents that don't have both documentuuid and originkey
  const validDocuments = documents.filter(
    (doc) => doc.documentuuid && doc.originkey,
  );

  if (validDocuments.length > DOCUMENT_STRIP_THRESHOLD) {
    const fadeEnd = theme.palette.background.paper;

    return (
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            marginBottom: 1.5,
            px: 0.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.secondary,
              fontSize: '0.8125rem',
            }}
          >
            {t('documents.documentsCount', { count: validDocuments.length })}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
            }}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{ color: 'inherit', fontSize: 'inherit' }}
            >
              {t('documents.scrollToViewAll')}
            </Typography>
            <ChevronRightIcon sx={{ fontSize: '1rem', opacity: 0.8 }} />
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minWidth: 0,
            marginLeft: { xs: -1, sm: -1.5 },
            marginRight: { xs: -1, sm: -1.5 },
            px: { xs: 1, sm: 1.5 },
          }}
        >
          <Box
            className="documents-strip"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth',
              pb: 1,
              pt: 0.5,
              mx: -0.5,
              // Scrollbar styling
              '&::-webkit-scrollbar': {
                height: '6px',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[500],
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[200],
                borderRadius: '8px',
              },
            }}
          >
            {validDocuments.map((doc, index) => (
              <Box
                key={index}
                sx={{
                  flexShrink: 0,
                  width: { xs: STRIP_CARD_WIDTH_XS, sm: STRIP_CARD_WIDTH },
                  minWidth: { xs: STRIP_CARD_WIDTH_XS, sm: STRIP_CARD_WIDTH },
                  scrollSnapAlign: 'start',
                  scrollSnapStop: 'normal',
                }}
              >
                <DocumentCard document={doc} variant="strip" />
              </Box>
            ))}
          </Box>

          {/* Fade on the right to suggest more content */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 8,
              width: 40,
              pointerEvents: 'none',
              background: `linear-gradient(to right, transparent, ${fadeEnd})`,
              borderRadius: '0 4px 4px 0',
            }}
          />
        </Box>
      </Box>
    );
  }

  // 1–3 documents: responsive grid with constrained width for single doc
  const count = validDocuments.length;
  const singleDocSize = {
    xs: 12,
    sm: 8,
    md: 6,
    lg: 5,
  };
  const multiDocSize = {
    xs: 12,
    sm: 6,
    md: count >= 3 ? 4 : 6,
    lg: count >= 4 ? 3 : count === 3 ? 4 : 6,
  };

  return (
    <Box
      sx={{
        marginTop: 2,
        marginBottom: 2,
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <Grid2
        container
        spacing={2}
        sx={{
          width: '100%',
          minWidth: 0,
        }}
      >
        {validDocuments.map((doc, index) => (
          <Grid2
            key={index}
            size={count === 1 ? singleDocSize : multiDocSize}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <DocumentCard document={doc} />
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
};

export default DocumentsList;
